import axios, {
  AxiosResponse,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import type { EventBus } from "@jt-home/jt-event-bus";
import { authEventBus } from "@jt-home/jt-auth-tools";
import {
  forceRootBasePaths,
  layerWhitelist,
  requestRefresh,
  requestUserInfo,
} from "./api.config";
import { localStorageAuth } from "./storageAuth";
import { matchPath } from "./matchPath";
import { createHandleLoginExpired } from "./handleLoginExpired";
import type {
  AuthEventMap,
  CreateAxiosConfig,
  CreateAxiosInstance,
  CustomAxiosRequestConfig,
  PathPattern,
  RequestHttpEventMap,
} from "./types";
import { setInstance } from "./axios-instance";

type AuthBus = {
  on: <K extends keyof AuthEventMap>(
    event: K,
    handler: AuthEventMap[K],
  ) => () => void;
  off: <K extends keyof AuthEventMap>(
    event: K,
    handler: AuthEventMap[K],
  ) => void;
  emit: <K extends keyof AuthEventMap>(
    event: K,
    ...args: Parameters<AuthEventMap[K]>
  ) => void;
  once: <K extends keyof AuthEventMap>(
    event: K,
    handler: AuthEventMap[K],
  ) => () => void;
  clear: (event?: keyof AuthEventMap) => void;
};

const AUTH_EVENT_NAME_MAP = {
  "changed:userInfo": "auth:user-info-changed",
  "changed:token": "auth:token-changed",
  unauthorized: "auth:unauthorized",
} as const;

function normalizeAuthEventName(
  event: keyof RequestHttpEventMap,
): keyof AuthEventMap {
  return (
    AUTH_EVENT_NAME_MAP[event as keyof typeof AUTH_EVENT_NAME_MAP] ?? event
  ) as keyof AuthEventMap;
}

function createCompatibleBus(
  source: AuthBus,
): EventBus<RequestHttpEventMap> {
  return {
    on<K extends keyof RequestHttpEventMap>(
      event: K,
      handler: RequestHttpEventMap[K],
    ) {
      return source.on(normalizeAuthEventName(event), handler as never);
    },
    off<K extends keyof RequestHttpEventMap>(
      event: K,
      handler: RequestHttpEventMap[K],
    ) {
      source.off(normalizeAuthEventName(event), handler as never);
    },
    emit<K extends keyof RequestHttpEventMap>(
      event: K,
      ...args: Parameters<RequestHttpEventMap[K]>
    ) {
      source.emit(
        normalizeAuthEventName(event),
        ...(args as Parameters<AuthEventMap[keyof AuthEventMap]>),
      );
    },
    once<K extends keyof RequestHttpEventMap>(
      event: K,
      handler: RequestHttpEventMap[K],
    ) {
      return source.once(normalizeAuthEventName(event), handler as never);
    },
    clear(event?: keyof RequestHttpEventMap) {
      // 兼容旧事件名，对外继续暴露旧接口，内部统一映射到 auth:* 事件。
      source.clear(event ? normalizeAuthEventName(event) : undefined);
    },
  };
}

const rawAuthBus = authEventBus as unknown as AuthBus;
export const requestHttpBus = createCompatibleBus(rawAuthBus);

function normalizeUserInfoPayload(result: unknown): unknown {
  if (result && typeof result === "object" && "data" in result) {
    return (result as { data?: unknown }).data;
  }

  return result;
}

function normalizeRootBaseURL(baseURL?: string): string {
  const normalizedBaseURL = baseURL?.trim();
  if (!normalizedBaseURL) {
    return "/api";
  }

  const withoutTrailingSlash = normalizedBaseURL.replace(/\/+$/, "");
  if (withoutTrailingSlash === "") {
    return "/api";
  }

  if (/\/api$/i.test(withoutTrailingSlash)) {
    return withoutTrailingSlash;
  }

  // Keep the old `/api` fallback for fixed auth endpoints.
  return `${withoutTrailingSlash}/api`;
}

function mergeWhitelist(
  layer: { noTokenPaths: PathPattern[]; skipRefreshPaths: PathPattern[] },
  project: CreateAxiosConfig,
): { noTokenPaths: PathPattern[]; skipRefreshPaths: PathPattern[] } {
  return {
    noTokenPaths: [...layer.noTokenPaths, ...(project.noTokenPaths ?? [])],
    skipRefreshPaths: [
      ...layer.skipRefreshPaths,
      ...(project.skipRefreshPaths ?? []),
    ],
  };
}

type HeadersLike =
  | Record<string, string>
  | {
      set?: (key: string, value: string) => void;
      get?: (key: string) => string | null;
    };

export function attachTokenToConfig(
  config: { headers?: HeadersLike },
  token: string,
  options?: { contentType?: string; accept?: string },
): void {
  const headers = config.headers;
  if (!headers) return;

  const contentType = options?.contentType ?? "application/json; charset=UTF-8";
  const accept = options?.accept ?? "application/json; charset=UTF-8";

  if (
    typeof (headers as { set?: (key: string, value: string) => void }).set ===
    "function"
  ) {
    const normalizedHeaders = headers as {
      set: (key: string, value: string) => void;
      get: (key: string) => string | null;
    };
    normalizedHeaders.set("x-access-token", token);
    if (!normalizedHeaders.get?.("Content-Type")) {
      normalizedHeaders.set("Content-Type", contentType);
    }
    normalizedHeaders.set("Accept", accept);
  } else {
    const normalizedHeaders = headers as Record<string, string>;
    normalizedHeaders["x-access-token"] = token;
    if (!normalizedHeaders["Content-Type"]) {
      normalizedHeaders["Content-Type"] = contentType;
    }
    normalizedHeaders.Accept = accept;
  }
}

export function createAxios(
  config: CreateAxiosConfig,
): AxiosInstance & CreateAxiosInstance {
  const {
    bus,
    getToken = localStorageAuth.getToken,
    setToken = localStorageAuth.setToken,
    getRefreshToken = localStorageAuth.getRefreshToken,
    setRefreshToken = localStorageAuth.setRefreshToken,
    clearAuth = localStorageAuth.clearAuth,
    onUnauthorized,
    loginExpiredCodes = ["401"],
    ...axiosConfig
  } = config;

  const whitelist = mergeWhitelist(layerWhitelist, config);
  const rootBaseURL = normalizeRootBaseURL(axiosConfig.baseURL);
  const compatBus = createCompatibleBus(
    (bus ? (bus as unknown as AuthBus) : rawAuthBus),
  );

  function fireUnauthorized(message?: string) {
    // Always emit the unified auth event, while keeping the old on/off facade.
    compatBus.emit("auth:unauthorized", message);
    onUnauthorized?.(message);
  }

  const instance = axios.create({
    baseURL: axiosConfig.baseURL,
    timeout: axiosConfig.timeout ?? 15000,
    headers: axiosConfig.headers,
    ...axiosConfig,
  });

  const innerInstance = axios.create({
    baseURL: axiosConfig.baseURL,
    timeout: axiosConfig.timeout ?? 15000,
    headers: axiosConfig.headers,
    ...axiosConfig,
  });

  let refreshPromise: Promise<void> | null = null;

  function shouldSkipToken(url: string): boolean {
    return matchPath(url, whitelist.noTokenPaths);
  }

  function shouldSkipRefresh(url: string): boolean {
    return matchPath(url, whitelist.skipRefreshPaths);
  }

  function shouldForceRootBase(url: string): boolean {
    return matchPath(url, forceRootBasePaths);
  }

  function doRefresh(): Promise<void> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearAuth();
      fireUnauthorized();
      return Promise.reject(new Error("No refresh token"));
    }

    return requestRefresh(refreshToken)
      .then((res) => {
        if (!res.accessToken) {
          clearAuth();
          fireUnauthorized();
          throw new Error("Refresh failed");
        }

        setToken(res.accessToken);
        if (res.refreshToken && setRefreshToken) {
          setRefreshToken(res.refreshToken);
        }

        compatBus.emit("auth:token-changed", res.accessToken);

        // Refresh success should keep token and userInfo updates in one chain.
        void requestUserInfo()
          .then((userInfo) => {
            compatBus.emit(
              "auth:user-info-changed",
              normalizeUserInfoPayload(userInfo),
            );
          })
          .catch((error) => {
            console.warn("[createAxios] getUserInfo after refresh failed:", error);
          });
      })
      .catch((error) => {
        clearAuth();
        fireUnauthorized();
        throw error;
      });
  }

  const handleLoginExpired = createHandleLoginExpired({
    instance: innerInstance,
    getRefreshToken,
    clearAuth,
    fireUnauthorized,
    shouldSkipRefresh,
    doRefresh,
    getRefreshPromise: () => refreshPromise,
    setRefreshPromise: (promise) => {
      refreshPromise = promise;
    },
  });

  instance.interceptors.request.use(
    async (req: InternalAxiosRequestConfig & CustomAxiosRequestConfig) => {
      const url = req.url ?? "";
      if (shouldForceRootBase(url)) {
        // Keep auth endpoints on the root /api base for legacy projects.
        req.baseURL = rootBaseURL;
      }

      const skipToken = shouldSkipToken(url);
      const skipRefresh =
        req.skipTokenRefresh === true || shouldSkipRefresh(url);

      if (refreshPromise && !skipToken && !skipRefresh) {
        await refreshPromise;
      }

      if (!skipToken) {
        const token = getToken();
        if (token) {
          attachTokenToConfig(req, token);
        }
      }

      if (!req.headers.get("Content-Type")) {
        req.headers.set("Content-Type", "application/json; charset=UTF-8");
      }

      return req;
    },
    (error) => Promise.reject(error),
  );

  innerInstance.interceptors.request.use(
    (req: InternalAxiosRequestConfig & CustomAxiosRequestConfig) => {
      const url = req.url ?? "";
      if (shouldForceRootBase(url)) {
        // innerInstance must share the same baseURL behavior.
        req.baseURL = rootBaseURL;
      }

      if (!shouldSkipToken(url)) {
        const token = getToken();
        if (token) {
          attachTokenToConfig(req, token);
        }
      }

      return req;
    },
    (error) => Promise.reject(error),
  );

  function isLoginExpiredCode(value: unknown): boolean {
    if (value === undefined || value === null) return false;
    return loginExpiredCodes.some((code) => String(code) === String(value));
  }

  function shouldHandleLoginExpiredFromError(error: unknown): boolean {
    const status = (error as { response?: { status?: unknown } })?.response?.status;
    const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
    return isLoginExpiredCode(status) || isLoginExpiredCode(code);
  }

  async function processResponse(response: unknown): Promise<unknown> {
    const code = (response as { data?: { code?: unknown } })?.data?.code;
    if (!isLoginExpiredCode(code)) return Promise.resolve(response);
    return handleLoginExpired(response as {
      config: InternalAxiosRequestConfig & CustomAxiosRequestConfig;
      data?: unknown;
    });
  }

  instance.interceptors.response.use(
    (response) =>
      processResponse(response) as Promise<import("axios").AxiosResponse>,
    (error) => {
      console.log(shouldHandleLoginExpiredFromError(error), '----');
      
      if (!shouldHandleLoginExpiredFromError(error)) {
        return Promise.reject(error);
      }

      // Let the refresh/unauthorized chain handle hard auth failures.
      return handleLoginExpired({
        config: (error as { config?: InternalAxiosRequestConfig & CustomAxiosRequestConfig })
          ?.config as InternalAxiosRequestConfig & CustomAxiosRequestConfig,
        response: (error as { response?: unknown })?.response,
      }) as Promise<AxiosResponse>;
    },
  );

  innerInstance.interceptors.response.use(
    (response) =>
      processResponse(response) as Promise<import("axios").AxiosResponse>,
    (error) => {
      console.log(shouldHandleLoginExpiredFromError(error), '----');
      
      if (!shouldHandleLoginExpiredFromError(error)) {
        return Promise.reject(error);
      }

      // Let the refresh/unauthorized chain handle hard auth failures.
      return handleLoginExpired({
        config: (error as { config?: InternalAxiosRequestConfig & CustomAxiosRequestConfig })
          ?.config as InternalAxiosRequestConfig & CustomAxiosRequestConfig,
        response: (error as { response?: unknown })?.response,
      }) as Promise<AxiosResponse>;
    },
  );

  setInstance(instance);

  return Object.assign(instance, {
    on: compatBus.on.bind(compatBus),
    off: compatBus.off.bind(compatBus),
    bus: compatBus,
  });
}

export {
  apiConfig,
  layerWhitelist,
  requestRefresh,
  requestUserInfo,
  requestFeishuSSOLogin,
} from "./api.config";
export {
  createLocalStorageAuth,
  localStorageAuth,
  getToken,
  setToken,
  getRefreshToken,
  setRefreshToken,
  clearAuth,
} from "./storageAuth";
export { matchPath } from "./matchPath";
export type {
  AuthEventMap,
  CreateAxiosConfig,
  CreateAxiosInstance,
  ApiConfig,
  WhitelistConfig,
  PathPattern,
  CustomAxiosRequestConfig,
  FeishuSSOLoginParams,
  FeishuSSOLoginResult,
  RefreshTokenResult,
  RequestHttpEventMap,
} from "./types";
export type { LocalStorageAuth, LocalStorageAuthOptions } from "./storageAuth";
export type {
  LoginExpiredContext,
  LoginExpiredPayload,
} from "./handleLoginExpired";
export { createHandleLoginExpired } from "./handleLoginExpired";
